import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Autocomplete,
    TextField,
    CircularProgress,
    Divider,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { GuestService } from '../../../api/services/guestService';
import { toast } from '../../../utils/toast';

export default function MergeGuestModal({ open, onClose, tenantId, onSuccess }) {
    const [primaryGuest, setPrimaryGuest] = useState(null);
    const [duplicateGuest, setDuplicateGuest] = useState(null);
    
    // Autocomplete states
    const [primaryOptions, setPrimaryOptions] = useState([]);
    const [duplicateOptions, setDuplicateOptions] = useState([]);
    const [loadingPrimary, setLoadingPrimary] = useState(false);
    const [loadingDuplicate, setLoadingDuplicate] = useState(false);
    
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Debounce helper
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };

    const fetchGuests = async (query, setOptions, setLoading) => {
        setLoading(true);
        try {
            const params = { noPagination: true };
            if (query) {
                params.search = query;
            }
            const res = await GuestService.searchGuests(params, tenantId);
            
            // The service returns the actual guest list array (or an object with items)
            const guestsArray = Array.isArray(res) ? res : (res?.data || res?.guests || res?.items || res?.content || []);
            setOptions(guestsArray);
        } catch (error) {
            console.error('Failed to search guests:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const debouncedFetchPrimary = useCallback(debounce((q) => fetchGuests(q, setPrimaryOptions, setLoadingPrimary), 500), [tenantId]);
    const debouncedFetchDuplicate = useCallback(debounce((q) => fetchGuests(q, setDuplicateOptions, setLoadingDuplicate), 500), [tenantId]);

    const handleMerge = async () => {
        if (!primaryGuest || !duplicateGuest) {
            toast.error('Please select both guests.');
            return;
        }
        if (primaryGuest.id === duplicateGuest.id) {
            toast.error('Cannot merge a guest into themselves.');
            return;
        }

        setIsSubmitting(true);
        try {
            await GuestService.mergeGuests({
                primaryGuestId: primaryGuest.id,
                duplicateGuestId: duplicateGuest.id,
                fieldOverrides: {}
            }, tenantId);
            
            toast.success('Guests merged successfully!');
            onSuccess();
            handleClose();
        } catch (error) {
            console.error('Merge error:', error);
            toast.error(error.response?.data?.message || 'Failed to merge guests.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setPrimaryGuest(null);
        setDuplicateGuest(null);
        setPrimaryOptions([]);
        setDuplicateOptions([]);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CompareArrowsIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="bold">Merge Guest Profiles</Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Merging will permanently transfer stays from the duplicate profile into the primary profile. The duplicate profile will be archived or removed.
                </Alert>

                <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary.main">
                        1. Select Primary Guest (Keeper)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                        This profile will be retained and updated. Search by name, email, or phone.
                    </Typography>
                    <Autocomplete
                        options={primaryOptions}
                        getOptionLabel={(option) => `${option.name || 'Unknown'} (${option.phone || option.email || 'No contact'})`}
                        loading={loadingPrimary}
                        value={primaryGuest}
                        onOpen={() => {
                            if (primaryOptions.length === 0) fetchGuests('', setPrimaryOptions, setLoadingPrimary);
                        }}
                        onChange={(e, newValue) => setPrimaryGuest(newValue)}
                        onInputChange={(e, newInputValue, reason) => {
                            if (reason === 'input') debouncedFetchPrimary(newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder="Search primary guest..."
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <React.Fragment>
                                            {loadingPrimary ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </React.Fragment>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Box>
                                    <Typography variant="body2" fontWeight="medium">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{option.email || option.phone}</Typography>
                                </Box>
                            </li>
                        )}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', my: -1 }}>
                    <CompareArrowsIcon sx={{ color: 'text.secondary', transform: 'rotate(90deg)' }} />
                </Box>

                <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="error.main">
                        2. Select Duplicate Guest (To be merged)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                        This profile will be merged into the primary and no longer be active.
                    </Typography>
                    <Autocomplete
                        options={duplicateOptions}
                        getOptionLabel={(option) => `${option.name || 'Unknown'} (${option.phone || option.email || 'No contact'})`}
                        loading={loadingDuplicate}
                        value={duplicateGuest}
                        onOpen={() => {
                            if (duplicateOptions.length === 0) fetchGuests('', setDuplicateOptions, setLoadingDuplicate);
                        }}
                        onChange={(e, newValue) => setDuplicateGuest(newValue)}
                        onInputChange={(e, newInputValue, reason) => {
                            if (reason === 'input') debouncedFetchDuplicate(newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder="Search duplicate guest..."
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <React.Fragment>
                                            {loadingDuplicate ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </React.Fragment>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Box>
                                    <Typography variant="body2" fontWeight="medium">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{option.email || option.phone}</Typography>
                                </Box>
                            </li>
                        )}
                    />
                </Box>

                {primaryGuest && duplicateGuest && primaryGuest.id === duplicateGuest.id && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                        Primary and Duplicate guests cannot be the same.
                    </Alert>
                )}
            </DialogContent>

            <Divider />
            <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
                <Button onClick={handleClose} disabled={isSubmitting} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleMerge}
                    variant="contained"
                    disabled={isSubmitting || !primaryGuest || !duplicateGuest || primaryGuest.id === duplicateGuest.id}
                    sx={{ px: 4 }}
                >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Merge Profiles'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
